import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Stack,
  LinearProgress
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { Event } from '@/types/events';

interface EventAnalyticsProps {
  event: Event;
  onUpdate: (updatedEvent: Event) => void;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

export const EventAnalytics: React.FC<EventAnalyticsProps> = ({
  event
}) => {
  const taskStats = {
    total: event.tasks.length,
    completed: event.tasks.filter(t => t.status === 'completed').length,
    inProgress: event.tasks.filter(t => t.status === 'in_progress').length,
    notStarted: event.tasks.filter(t => t.status === 'not_started').length
  };

  const budgetStats = {
    estimated: event.budget.reduce((sum, item) => sum + item.estimatedCost, 0),
    actual: event.budget.reduce((sum, item) => sum + (item.actualCost || 0), 0)
  };

  const taskData = [
    { name: 'Completed', value: taskStats.completed },
    { name: 'In Progress', value: taskStats.inProgress },
    { name: 'Not Started', value: taskStats.notStarted }
  ];

  const budgetData = [
    { name: 'Estimated', amount: budgetStats.estimated },
    { name: 'Actual', amount: budgetStats.actual }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Typography variant="h6">Event Analytics</Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Task Progress
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={taskData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label
                    >
                      {taskData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Budget Overview
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={budgetData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="amount" fill="#8884d8" name="Amount ($)" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Compliance Status
              </Typography>
              <Stack spacing={2}>
                {['completed', 'pending', 'not_applicable'].map((status) => {
                  const count = event.compliance.filter(item => item.status === status).length;
                  const percentage = (count / event.compliance.length) * 100;
                  
                  return (
                    <Box key={status}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">
                          {status.replace('_', ' ')}
                        </Typography>
                        <Typography variant="body2">
                          {percentage.toFixed(1)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};